import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, RefreshCw, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getH1 } from '../seo/seoRoutes.mjs';

const CareerSpreadPage = () => {
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const [selectedCardCount, setSelectedCardCount] = useState(null);
  const [selectedCareerType, setSelectedCareerType] = useState(null);
  const [question, setQuestion] = useState('');
  const [currentQuote, setCurrentQuote] = useState('');

  // Card count options with their meanings
  const cardOptions = [
    { count: 1, name: t('cards.singleCard'), description: t('career.singleCardDesc') },
    { count: 3, name: t('cards.pastPresentFuture'), description: t('career.pastPresentFutureDesc') },
    { count: 5, name: t('career.careerCross'), description: t('career.careerCrossDesc') },
    { count: 6, name: t('career.professionalPath'), description: t('career.professionalPathDesc') },
    { count: 7, name: t('career.careerChakra'), description: t('career.careerChakraDesc') },
    { count: 10, name: t('cards.celticCross'), description: t('career.celticCrossDesc') },
    { count: 12, name: t('career.zodiacCareer'), description: t('career.zodiacCareerDesc') }
  ];

  // Career type options
  const careerTypes = [
    {
      type: "current-job",
      name: t('career.currentJob'),
      icon: "icon_04_biz_v2.png",
      color: "#E7FBBE",
      description: t('career.currentJobDesc'),
      focusAreas: [t('career.preparationStrategies'), t('career.firstImpressions'), t('career.integrationTips')]
    },
    {
      type: "job-seeking",
      name: t('career.jobSeeking'),
      icon: "icon_01_find_v2.png",
      color: "#FFFDDE",
      description: t('career.jobSeekingDesc'),
      focusAreas: [t('career.identifyPassions'), t('career.marketOpportunities'), t('career.manifestationTechniques')]
    },
    {
      type: "entrepreneur",
      name: t('career.entrepreneur'),
      icon: "icon_02_love_v2.png",
      color: "#FFCBCB",
      description: t('career.entrepreneurDesc'),
      focusAreas: [t('career.soulPurpose'), t('career.naturalTalents'), t('career.serviceToOthers')]
    },
    {
      type: "career-path",
      name: t('career.developCareerPath'),
      icon: "icon_03_family_v2.png",
      color: "#7BD3EA",
      description: t('career.careerPathDesc'),
      focusAreas: [t('career.strategicPlanning'), t('career.skillDevelopment'), t('career.longTermVision')]
    },
    {
      type: "workplace-collaboration",
      name: t('career.workplaceCollaboration'),
      icon: "icon_04_biz_v2.png",
      color: "#DDA0DD",
      description: t('career.workplaceCollaborationDesc'),
      focusAreas: [t('career.communicationSkills'), t('career.teamDynamics'), t('career.leadershipPresence')]
    }
  ];

  // Spread interpretations based on career type and card count
  const getSpreadInterpretations = (type, count) => {
    const interpretations = {
      "current-job": {
        1: [t('career.spread.newJob.1.position1')],
        3: [t('career.spread.newJob.3.position1'), t('career.spread.newJob.3.position2'), t('career.spread.newJob.3.position3')],
        5: [t('career.spread.newJob.5.position1'), t('career.spread.newJob.5.position2'), t('career.spread.newJob.5.position3'), t('career.spread.newJob.5.position4'), t('career.spread.newJob.5.position5')],
        6: [t('career.spread.newJob.6.position1'), t('career.spread.newJob.6.position2'), t('career.spread.newJob.6.position3'), t('career.spread.newJob.6.position4'), t('career.spread.newJob.6.position5'), t('career.spread.newJob.6.position6')],
        7: [t('career.spread.newJob.7.position1'), t('career.spread.newJob.7.position2'), t('career.spread.newJob.7.position3'), t('career.spread.newJob.7.position4'), t('career.spread.newJob.7.position5'), t('career.spread.newJob.7.position6'), t('career.spread.newJob.7.position7')],
        10: [t('career.spread.newJob.10.position1'), t('career.spread.newJob.10.position2'), t('career.spread.newJob.10.position3'), t('career.spread.newJob.10.position4'), t('career.spread.newJob.10.position5'), t('career.spread.newJob.10.position6'), t('career.spread.newJob.10.position7'), t('career.spread.newJob.10.position8'), t('career.spread.newJob.10.position9'), t('career.spread.newJob.10.position10')],
        12: [t('career.spread.newJob.12.position1'), t('career.spread.newJob.12.position2'), t('career.spread.newJob.12.position3'), t('career.spread.newJob.12.position4'), t('career.spread.newJob.12.position5'), t('career.spread.newJob.12.position6'), t('career.spread.newJob.12.position7'), t('career.spread.newJob.12.position8'), t('career.spread.newJob.12.position9'), t('career.spread.newJob.12.position10'), t('career.spread.newJob.12.position11'), t('career.spread.newJob.12.position12')]
      },
      "job-seeking": {
        1: [t('career.spread.dreamJob.1.position1')],
        3: [t('career.spread.dreamJob.3.position1'), t('career.spread.dreamJob.3.position2'), t('career.spread.dreamJob.3.position3')],
        5: [t('career.spread.dreamJob.5.position1'), t('career.spread.dreamJob.5.position2'), t('career.spread.dreamJob.5.position3'), t('career.spread.dreamJob.5.position4'), t('career.spread.dreamJob.5.position5')],
        6: [t('career.spread.dreamJob.6.position1'), t('career.spread.dreamJob.6.position2'), t('career.spread.dreamJob.6.position3'), t('career.spread.dreamJob.6.position4'), t('career.spread.dreamJob.6.position5'), t('career.spread.dreamJob.6.position6')],
        7: [t('career.spread.dreamJob.7.position1'), t('career.spread.dreamJob.7.position2'), t('career.spread.dreamJob.7.position3'), t('career.spread.dreamJob.7.position4'), t('career.spread.dreamJob.7.position5'), t('career.spread.dreamJob.7.position6'), t('career.spread.dreamJob.7.position7')],
        10: [t('career.spread.dreamJob.10.position1'), t('career.spread.dreamJob.10.position2'), t('career.spread.dreamJob.10.position3'), t('career.spread.dreamJob.10.position4'), t('career.spread.dreamJob.10.position5'), t('career.spread.dreamJob.10.position6'), t('career.spread.dreamJob.10.position7'), t('career.spread.dreamJob.10.position8'), t('career.spread.dreamJob.10.position9'), t('career.spread.dreamJob.10.position10')],
        12: [t('career.spread.dreamJob.12.position1'), t('career.spread.dreamJob.12.position2'), t('career.spread.dreamJob.12.position3'), t('career.spread.dreamJob.12.position4'), t('career.spread.dreamJob.12.position5'), t('career.spread.dreamJob.12.position6'), t('career.spread.dreamJob.12.position7'), t('career.spread.dreamJob.12.position8'), t('career.spread.dreamJob.12.position9'), t('career.spread.dreamJob.12.position10'), t('career.spread.dreamJob.12.position11'), t('career.spread.dreamJob.12.position12')]
      },
      "entrepreneur": {
        1: [t('career.spread.lifePurpose.1.position1')],
        3: [t('career.spread.lifePurpose.3.position1'), t('career.spread.lifePurpose.3.position2'), t('career.spread.lifePurpose.3.position3')],
        5: [t('career.spread.lifePurpose.5.position1'), t('career.spread.lifePurpose.5.position2'), t('career.spread.lifePurpose.5.position3'), t('career.spread.lifePurpose.5.position4'), t('career.spread.lifePurpose.5.position5')],
        6: [t('career.spread.lifePurpose.6.position1'), t('career.spread.lifePurpose.6.position2'), t('career.spread.lifePurpose.6.position3'), t('career.spread.lifePurpose.6.position4'), t('career.spread.lifePurpose.6.position5'), t('career.spread.lifePurpose.6.position6')],
        7: [t('career.spread.lifePurpose.7.position1'), t('career.spread.lifePurpose.7.position2'), t('career.spread.lifePurpose.7.position3'), t('career.spread.lifePurpose.7.position4'), t('career.spread.lifePurpose.7.position5'), t('career.spread.lifePurpose.7.position6'), t('career.spread.lifePurpose.7.position7')],
        10: [t('career.spread.lifePurpose.10.position1'), t('career.spread.lifePurpose.10.position2'), t('career.spread.lifePurpose.10.position3'), t('career.spread.lifePurpose.10.position4'), t('career.spread.lifePurpose.10.position5'), t('career.spread.lifePurpose.10.position6'), t('career.spread.lifePurpose.10.position7'), t('career.spread.lifePurpose.10.position8'), t('career.spread.lifePurpose.10.position9'), t('career.spread.lifePurpose.10.position10')],
        12: [t('career.spread.lifePurpose.12.position1'), t('career.spread.lifePurpose.12.position2'), t('career.spread.lifePurpose.12.position3'), t('career.spread.lifePurpose.12.position4'), t('career.spread.lifePurpose.12.position5'), t('career.spread.lifePurpose.12.position6'), t('career.spread.lifePurpose.12.position7'), t('career.spread.lifePurpose.12.position8'), t('career.spread.lifePurpose.12.position9'), t('career.spread.lifePurpose.12.position10'), t('career.spread.lifePurpose.12.position11'), t('career.spread.lifePurpose.12.position12')]
      },
      "career-path": {
        1: [t('career.spread.careerPath.1.position1')],
        3: [t('career.spread.careerPath.3.position1'), t('career.spread.careerPath.3.position2'), t('career.spread.careerPath.3.position3')],
        5: [t('career.spread.careerPath.5.position1'), t('career.spread.careerPath.5.position2'), t('career.spread.careerPath.5.position3'), t('career.spread.careerPath.5.position4'), t('career.spread.careerPath.5.position5')],
        6: [t('career.spread.careerPath.6.position1'), t('career.spread.careerPath.6.position2'), t('career.spread.careerPath.6.position3'), t('career.spread.careerPath.6.position4'), t('career.spread.careerPath.6.position5'), t('career.spread.careerPath.6.position6')],
        7: [t('career.spread.careerPath.7.position1'), t('career.spread.careerPath.7.position2'), t('career.spread.careerPath.7.position3'), t('career.spread.careerPath.7.position4'), t('career.spread.careerPath.7.position5'), t('career.spread.careerPath.7.position6'), t('career.spread.careerPath.7.position7')],
        10: [t('career.spread.careerPath.10.position1'), t('career.spread.careerPath.10.position2'), t('career.spread.careerPath.10.position3'), t('career.spread.careerPath.10.position4'), t('career.spread.careerPath.10.position5'), t('career.spread.careerPath.10.position6'), t('career.spread.careerPath.10.position7'), t('career.spread.careerPath.10.position8'), t('career.spread.careerPath.10.position9'), t('career.spread.careerPath.10.position10')],
        12: [t('career.spread.careerPath.12.position1'), t('career.spread.careerPath.12.position2'), t('career.spread.careerPath.12.position3'), t('career.spread.careerPath.12.position4'), t('career.spread.careerPath.12.position5'), t('career.spread.careerPath.12.position6'), t('career.spread.careerPath.12.position7'), t('career.spread.careerPath.12.position8'), t('career.spread.careerPath.12.position9'), t('career.spread.careerPath.12.position10'), t('career.spread.careerPath.12.position11'), t('career.spread.careerPath.12.position12')]
      },
      "workplace-collaboration": {
        1: [t('career.spread.workplace.1.position1')],
        3: [t('career.spread.workplace.3.position1'), t('career.spread.workplace.3.position2'), t('career.spread.workplace.3.position3')],
        5: [t('career.spread.workplace.5.position1'), t('career.spread.workplace.5.position2'), t('career.spread.workplace.5.position3'), t('career.spread.workplace.5.position4'), t('career.spread.workplace.5.position5')],
        6: [t('career.spread.workplace.6.position1'), t('career.spread.workplace.6.position2'), t('career.spread.workplace.6.position3'), t('career.spread.workplace.6.position4'), t('career.spread.workplace.6.position5'), t('career.spread.workplace.6.position6')],
        7: [t('career.spread.workplace.7.position1'), t('career.spread.workplace.7.position2'), t('career.spread.workplace.7.position3'), t('career.spread.workplace.7.position4'), t('career.spread.workplace.7.position5'), t('career.spread.workplace.7.position6'), t('career.spread.workplace.7.position7')],
        10: [t('career.spread.workplace.10.position1'), t('career.spread.workplace.10.position2'), t('career.spread.workplace.10.position3'), t('career.spread.workplace.10.position4'), t('career.spread.workplace.10.position5'), t('career.spread.workplace.10.position6'), t('career.spread.workplace.10.position7'), t('career.spread.workplace.10.position8'), t('career.spread.workplace.10.position9'), t('career.spread.workplace.10.position10')],
        12: [t('career.spread.workplace.12.position1'), t('career.spread.workplace.12.position2'), t('career.spread.workplace.12.position3'), t('career.spread.workplace.12.position4'), t('career.spread.workplace.12.position5'), t('career.spread.workplace.12.position6'), t('career.spread.workplace.12.position7'), t('career.spread.workplace.12.position8'), t('career.spread.workplace.12.position9'), t('career.spread.workplace.12.position10'), t('career.spread.workplace.12.position11'), t('career.spread.workplace.12.position12')]
      }
    };
    
    return interpretations[type]?.[count] || [`Professional Guidance Position ${Array.from({length: count}, (_, i) => i + 1).join(', Position ')}`];
  };

  useEffect(() => {
    // Set initial random quote
    const careerQuotes = t('quotes.career');
    const randomQuote = careerQuotes[Math.floor(Math.random() * careerQuotes.length)];
    setCurrentQuote(randomQuote);
  }, [t]);

  // Auto-scroll to career type section when card count is selected
  useEffect(() => {
    if (selectedCardCount) {
      const careerSection = document.querySelector('.relationship-type-section');
      if (careerSection) {
        careerSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedCardCount]);

  // Auto-scroll to question section when career type is selected
  useEffect(() => {
    if (selectedCareerType) {
      const questionSection = document.querySelector('.question-section');
      if (questionSection) {
        questionSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedCareerType]);

  const startCareerReading = () => {
    if (!selectedCardCount || !selectedCareerType) return;

    const positions = getSpreadInterpretations(selectedCareerType, selectedCardCount);
    
    // Navigate to SpreadDrawingPage with career spread configuration
    navigate('/spread-drawing', {
      state: {
        spreadName: `${careerTypes.find(t => t.type === selectedCareerType)?.name} - ${cardOptions.find(o => o.count === selectedCardCount)?.name}`,
        spreadType: selectedCardCount === 1 ? 'single' : 'multiple',
        cardCount: selectedCardCount,
        spreadInterpretations: positions,
        question: question,
        returnPath: "/career-spread",
        spreadConfig: {
          category: "Career & Purpose",
          careerType: selectedCareerType,
          isSingleCard: selectedCardCount === 1
        }
      }
    });
  };

  const resetSpread = () => {
    setSelectedCardCount(null);
    setSelectedCareerType(null);
    setQuestion('');
    // Set new random quote
    const careerQuotes = t('quotes.career');
    const randomQuote = careerQuotes[Math.floor(Math.random() * careerQuotes.length)];
    setCurrentQuote(randomQuote);
  };

  return (
    <div className="career-spread-page">
      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1 className="welcome-subtitle">
              {getH1('/career-spread', currentLanguage)}
            </h1>
            <blockquote className="inspiration-question">
              {currentQuote}
            </blockquote>
            <button 
              className="draw-cards-btn"
              onClick={() => {
                navigate('/spread-drawing', { 
                  state: { 
                    spreadName: t('career.careerCardOfDay'),
                    spreadType: 'single',
                    cardCount: 1,
                    spreadInterpretations: [t('dailyCard.careerPosition')],
                    question: currentQuote,
                    returnPath: "/career-spread",
                    spreadConfig: {
                      isSingleCard: true,
                      isDailyCard: true
                    }
                  } 
                })
              }}
            >
              <Sparkles size={20} />
              {t('career.drawCareerCard')}
            </button>
          </div>
          <div className="welcome-decoration"></div>
        </div>
      </section>

      <div className="card-selection-section">
        <h2>1. {t('career.chooseCareerSpread')}</h2>
        <p className="selection-subtitle">{t('career.selectNumberCards')}</p>
        
        <div className="card-options-grid">
          {cardOptions.map((option) => (
            <div
              key={option.count}
              className={`card-option ${selectedCardCount === option.count ? 'selected' : ''}`}
              onClick={() => setSelectedCardCount(option.count)}
            >
              <span className="card-count">{option.count}</span>
              <div className="option-content">
                <span className="option-name">{option.name}</span>
                <p className="option-description">{option.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Career Type Section */}
        <div className="relationship-type-section">
          <h2>2. {t('career.careerType')}</h2>
          <p className="selection-subtitle">{t('career.selectCareerGuidance')}</p>
          
          <div className="relationship-types-grid">
            {careerTypes.map((careerType) => (
              <div
                key={careerType.type}
                className={`relationship-type-card ${selectedCareerType === careerType.type ? 'selected' : ''}`}
                onClick={() => setSelectedCareerType(careerType.type)}
                style={{ '--type-color': careerType.color }}
              >
                <div className="type-icon">
                  <img 
                    src={`/images/${careerType.icon}`} 
                    alt={careerType.name}
                    style={{ width: '3rem', height: '3rem' }}
                  />
                </div>
                <h3 className="type-name">{careerType.name}</h3>
                <p className="type-description">{careerType.description}</p>
                <div className="focus-areas">
                  <small>{t('career.focusAreas')}</small>
                  <ul>
                    {careerType.focusAreas.map((area, index) => (
                      <li key={index}>{area}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCardCount && selectedCareerType && (
          <div className="question-section">
            <h2>3. {t('career.focusIntention')}</h2>
            <p className="selection-subtitle">{t('career.centerMindQuestion')}</p>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('career.questionPlaceholder')}
              className="question-input"
              rows="3"
            />
            
            <button 
              onClick={startCareerReading} 
              className="draw-cards-btn"
            >
              <Briefcase size={20} />
              {t('career.startCareerReading')}
            </button>

            <button 
              onClick={resetSpread} 
              className="secondary-btn"
              style={{ marginLeft: '1rem' }}
            >
              <RefreshCw size={16} />
              {t('career.reset')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareerSpreadPage;
